import "../UserManagement.css";
import { useState, useMemo, useEffect, useRef } from "react";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow
} from "../../ui/table";
import {
    UserPlus,
    Search,
    Lock,
    Users,
    Calendar,
    Clock,
    Shield,
    Eye,
    EyeOff,
    Tags,
    MoreHorizontal,
    Trash2,
} from "lucide-react";
import CardPagination from "../CardPagination";
import EndpointActionButton from "../Endpoints/EndpointActionButton";
import InviteUserDialog from "./InviteUserDialog";
import { deleteInvitation, fetchAllDevices, fetchAllRoles, fetchPendingUsers } from "./Api/inviteuserdetails";
import { myLocalStorage } from "../../StorageHelper";
import { Role, UserVerification } from "./interface";
import { Device } from "../interfaces/EndpointInterfaces";
import { formatDate } from "../formatTimeAgo";
import useUserStore from "../../../services/userStore";
import {
    LIST_STATE_KEYS,
    parsePersistedNumber,
    parsePersistedString,
    readPersistedListState,
    writePersistedListState,
} from "../../../utils/listPaginationState";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "../../ui/dropdown-menu";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "../../ui/alert-dialog";
import { useToast } from "../../../hooks/use-toast";


const ITEMS_PER_PAGE = 10;

export default function InviteUserDetails() {
    const savedState = readPersistedListState(LIST_STATE_KEYS.inviteUsers);
    const [searchTerm, setSearchTerm] = useState(parsePersistedString(savedState.searchTerm));
    const [currentPage, setCurrentPage] = useState(parsePersistedNumber(savedState.currentPage, 1));
    const [showInviteDialog, setShowInviteDialog] = useState(false);
    const [activeTab, setActiveTab] = useState(parsePersistedString(savedState.activeTab, "user"));
    const skipTabPageResetRef = useRef(true);
    const tenantName = myLocalStorage.getItem("latestTenant").tenantName || "";
    const [users, setUsers] = useState<any[]>([]);
    const [allDevices, setAllDevices] = useState<Device[]>([]);
    const [visibleCodes, setVisibleCodes] = useState<Set<string>>(new Set());
    const [adminRoles, setAdminRoles] = useState<Role[] | []>([])
    const [userRoles, setUserRoles] = useState<Role[] | []>([])
    const { userRole } = useUserStore((state) => state.user);
    const { toast } = useToast();
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [selectedUser, setSelectedUser] = useState<UserVerification | null>(null);
    const [actionLoading, setActionLoading] = useState(false);


    // Filter users based on search and tab
    const filteredUsers = useMemo(() => {
        const tabFiltered = users.filter(user =>
            activeTab === "admin" ? user.type === "admin" : user.type === "user"
        );

        if (!searchTerm) return tabFiltered;

        const searchLower = searchTerm.toLowerCase();
        return tabFiltered.filter(user =>
            user.firstName.toLowerCase().includes(searchLower) ||
            user.lastName.toLowerCase().includes(searchLower) ||
            user.email.toLowerCase().includes(searchLower)
        );
    }, [searchTerm, activeTab, users]);

    // Paginate filtered users
    const paginatedUsers = useMemo(() => {
        const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
        const endIndex = startIndex + ITEMS_PER_PAGE;
        return filteredUsers.slice(startIndex, endIndex);
    }, [filteredUsers, currentPage]);

    const totalPages = Math.ceil(filteredUsers.length / ITEMS_PER_PAGE);

    const handleSearchChange = (value: string) => {
        setSearchTerm(value);
        setCurrentPage(1);
    };

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
    };




    // Function to toggle visibility
    const toggleVerificationCode = (userId: string) => {
        setVisibleCodes(prev => {
            const newSet = new Set(prev);
            if (newSet.has(userId)) {
                newSet.delete(userId);
            } else {
                newSet.add(userId);
            }
            return newSet;
        });
    };


    const generateVerificationCode = (user: UserVerification) => {
        return user.verificationCode;
    };

    const [nowTick, setNowTick] = useState(Date.now());

    useEffect(() => {
        const interval = setInterval(() => setNowTick(Date.now()), 60_000);
        return () => clearInterval(interval);
    }, []);

    const parseExpiryDate = (expiresAt?: string) => {
        if (!expiresAt) return null;
        const trimmed = expiresAt.trim();
        const dotted = trimmed.match(/^(\d{2})\.(\d{2})\.(\d{4}) (\d{2}):(\d{2})/);
        if (dotted) {
            return new Date(`${dotted[3]}-${dotted[2]}-${dotted[1]}T${dotted[4]}:${dotted[5]}:00`);
        }
        const parsed = new Date(trimmed);
        return Number.isNaN(parsed.getTime()) ? null : parsed;
    };

    const getHoursRemaining = (expiresAt: string) => {
        const expiry = parseExpiryDate(expiresAt);
        if (!expiry) return 0;
        const diff = expiry.getTime() - nowTick;
        return Math.max(0, Math.round(diff / (1000 * 60 * 60)));
    };

    const formatHoursRemaining = (hours: number) => {
        if (hours <= 0) return 'Expired';
        if (hours < 24) return `${hours}h`;
        const days = Math.floor(hours / 24);
        const remainingHours = hours % 24;
        return remainingHours > 0 ? `${days}d ${remainingHours}h` : `${days}d`;
    };

    const getExpiryVariant = (hours: number) => {
        if (hours <= 0) return 'danger';
        if (hours < 24) return 'warning';
        return 'success';
    };

    const getAllDevices = async () => {
        const devices: Device[] = await fetchAllDevices(tenantName);
        setAllDevices(devices);
    }

    const loadUsers = async () => {
        const pending: UserVerification[] = await fetchPendingUsers(tenantName);
        const roles: Role[] = await fetchAllRoles(tenantName);

        setAdminRoles(roles.filter(role => role.roleType === "admin") || []);
        setUserRoles(roles.filter(role => role.roleType !== "admin" && role.roleName !== "super-user") || [])

        const usersWithTypeRolesAndTags = pending.map(user => {
            const roleNames = user.roleName.split(",").map(r => r.trim());
            const machines = (user.machines || "").split(",").map(r => r.trim());
            const isAdmin = roleNames.some(r =>
                roles.find(role => role.roleName === r && role.roleType === "admin")
            );

            const tagList = user.tags ? user.tags.split(",").map(t => t.trim()).filter(Boolean) : [];

            return {
                ...user,
                type: isAdmin ? "admin" : "user",
                roles: roleNames,
                tags: tagList,
                machines: machines
            };
        });

        setUsers(usersWithTypeRolesAndTags);
    };


    useEffect(() => {
        if (!tenantName) return;
        loadUsers();
        getAllDevices();
    }, [tenantName])

    useEffect(() => {
        writePersistedListState(LIST_STATE_KEYS.inviteUsers, {
            currentPage,
            searchTerm,
            activeTab,
        });
    }, [currentPage, searchTerm, activeTab]);

    useEffect(() => {
        if (skipTabPageResetRef.current) {
            skipTabPageResetRef.current = false;
            return;
        }
        setCurrentPage(1);
    }, [activeTab]);


    const handleDeleteClick = (user: UserVerification) => {
        setSelectedUser(user);
        setDeleteDialogOpen(true);
    };


    const handleDeleteConfirm = async () => {
        if (!selectedUser || !tenantName) return;
        setActionLoading(true);
        try {
            const response = await deleteInvitation(tenantName, selectedUser.email);
            if (response?.isOk) {
                toast({
                    title: "Invitation Deleted",
                    description: `Deleted invitation for ${selectedUser.email}`,
                });
                await loadUsers();
            } else {
                toast({
                    title: "Delete Failed",
                    description: response?.errorMessage === "INVITATION_NOT_FOUND"
                        ? "Invitation not found or already removed."
                        : "Failed to delete invitation.",
                    variant: "destructive",
                });
            }
        } catch (error) {
            console.error("Failed to delete invitation:", error);
            toast({
                title: "Delete Failed",
                description: "Failed to delete invitation.",
                variant: "destructive",
            });
        } finally {
            setActionLoading(false);
            setDeleteDialogOpen(false);
            setSelectedUser(null);
        }
    };


    return (
        <div className="um-page um-directory-page">
            <div className="um-card um-toolbar um-directory-toolbar">
                <div className="um-title">
                    <span className="um-title-icon" aria-hidden="true">
                        <UserPlus />
                    </span>
                    <h1 className="um-title-text" data-testid="text-invite-users-title">
                        Pending User Verifications
                        <span className="um-title-count">{filteredUsers.length}</span>
                    </h1>
                </div>
                <div className="um-toolbar-actions">
                    {userRole !== "epam-admin" &&
                        userRole !== "server-pam-admin" &&
                        <div className="um-segmented" role="tablist" aria-label="Invitation type">
                            <button
                                type="button"
                                role="tab"
                                aria-selected={activeTab === "admin"}
                                data-active={activeTab === "admin"}
                                onClick={() => setActiveTab("admin")}
                                data-testid="tab-admin-invites"
                            >
                                <Shield />
                                Admin
                            </button>
                            <button
                                type="button"
                                role="tab"
                                aria-selected={activeTab === "user"}
                                data-active={activeTab === "user"}
                                onClick={() => setActiveTab("user")}
                                data-testid="tab-user-invites"
                            >
                                <Users />
                                Users
                            </button>
                        </div>
                    }
                    <div className="um-search">
                        <Search aria-hidden="true" />
                        <Input
                            placeholder="Search users..."
                            aria-label="Search users"
                            value={searchTerm}
                            onChange={(e) => handleSearchChange(e.target.value)}
                            data-testid="input-search-users"
                        />
                    </div>
                    <EndpointActionButton
                        onClick={() => setShowInviteDialog(true)}
                        data-testid="button-invite-now"
                    >
                        <UserPlus className="w-4 h-4" />
                        Invite User
                    </EndpointActionButton>
                </div>
            </div>

            <div className="um-card um-table-card">
                <div className="um-table-scroll">
                    <Table className="um-table">
                        <TableHeader>
                            <TableRow>
                                <TableHead>
                                    <div className="flex items-center gap-1.5">
                                        <Calendar />
                                        Created At
                                    </div>
                                </TableHead>
                                <TableHead>Name</TableHead>
                                <TableHead>Email</TableHead>
                                <TableHead className="text-center">
                                    <div className="flex items-center justify-center gap-1.5">
                                        <Lock />
                                        Verification Code
                                    </div>
                                </TableHead>
                                <TableHead>Roles</TableHead>
                                {activeTab === "admin" && <TableHead>Tags</TableHead>}
                                {activeTab === "user" && <TableHead>Machines</TableHead>}
                                <TableHead className="text-center">
                                    <div className="flex items-center justify-center gap-1.5">
                                        <Clock />
                                        Expires In
                                    </div>
                                </TableHead>
                                <TableHead className="text-center">Actions</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {paginatedUsers.length > 0 ? paginatedUsers.map((user) => {
                                const hoursRemaining = getHoursRemaining(user.expiredAt);
                                return (
                                    <TableRow
                                        key={user.email}
                                        data-testid={`row-user-${user.id}`}
                                    >
                                        <TableCell className="um-cell-muted" data-testid={`created-at-${user.id}`}>
                                            {formatDate(user.createdAt)}
                                        </TableCell>
                                        <TableCell>
                                            <div className="um-identity">
                                                <span className="um-avatar" aria-hidden="true">
                                                    {`${user.firstName?.[0] ?? ""}${user.lastName?.[0] ?? ""}`}
                                                </span>
                                                <div className="min-w-0">
                                                    <div className="um-name" data-testid={`name-${user.id}`}>
                                                        {user.firstName} {user.lastName}
                                                    </div>
                                                    <div className="um-name-sub">{user.type}</div>
                                                </div>
                                            </div>
                                        </TableCell>
                                        <TableCell data-testid={`email-${user.id}`}>
                                            <span className="um-email">{user.email}</span>
                                        </TableCell>
                                        <TableCell className="text-center">
                                            <div className="um-code">
                                                {visibleCodes.has(user.email) ? (
                                                    <>
                                                        <span className="um-code-value" data-testid={`verification-code-${user.id}`}>
                                                            {generateVerificationCode(user)}
                                                        </span>
                                                        <Button
                                                            variant="outline"
                                                            size="icon"
                                                            className="um-icon-button no-default-hover-elevate"
                                                            onClick={() => toggleVerificationCode(user.email)}
                                                            aria-label="Hide verification code"
                                                            data-testid={`hide-code-${user.id}`}
                                                        >
                                                            <EyeOff />
                                                        </Button>
                                                    </>
                                                ) : (
                                                    <>
                                                        <Shield aria-hidden="true" />
                                                        <Button
                                                            variant="outline"
                                                            size="icon"
                                                            className="um-icon-button no-default-hover-elevate"
                                                            onClick={() => toggleVerificationCode(user.email)}
                                                            aria-label="Show verification code"
                                                            data-testid={`show-code-${user.id}`}
                                                        >
                                                            <Eye />
                                                        </Button>
                                                    </>
                                                )}
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <div className="um-chips">
                                                {user.roles?.map((role: string) => (
                                                    <span
                                                        key={role}
                                                        className="um-chip"
                                                        data-tone="primary"
                                                        data-testid={`badge-role-${role}`}
                                                    >
                                                        {role}
                                                    </span>
                                                ))}
                                            </div>
                                        </TableCell>
                                        {activeTab === "admin" && (
                                            <TableCell>
                                                <div className="um-chips">
                                                    {user.tags && user.tags.length > 0 ? (
                                                        user.tags?.map((tag: any) => (
                                                            <span
                                                                key={tag}
                                                                className="um-chip"
                                                                data-testid={`badge-tag-${tag}`}
                                                            >
                                                                <Tags aria-hidden="true" />
                                                                {tag}
                                                            </span>
                                                        ))
                                                    ) : (
                                                        <span className="um-dash">-</span>
                                                    )}
                                                </div>
                                            </TableCell>
                                        )}
                                        {activeTab === "user" && (
                                            <TableCell data-testid={`machines-${user.id}`}>
                                                {user.machines && user.machines.length > 0 ? (
                                                    <div className="um-chips">
                                                        {user.machines.slice(0, 2).map((machine: any) => (
                                                            <span
                                                                key={machine}
                                                                className="um-chip"
                                                                data-mono="true"
                                                                data-testid={`badge-machine-${machine}`}
                                                            >
                                                                {machine}
                                                            </span>
                                                        ))}
                                                        {user.machines.length > 2 && (
                                                            <span className="um-chip">
                                                                +{user.machines.length - 2} more
                                                            </span>
                                                        )}
                                                    </div>
                                                ) : (
                                                    <span className="um-dash">-</span>
                                                )}
                                            </TableCell>
                                        )}
                                        <TableCell className="text-center" data-testid={`expires-in-${user.id}`}>
                                            <span className="um-chip" data-tone={getExpiryVariant(hoursRemaining)}>
                                                <Clock aria-hidden="true" />
                                                {hoursRemaining <= 0 ? 'Expired' : `${formatHoursRemaining(hoursRemaining)}`}
                                            </span>
                                        </TableCell>
                                        <TableCell className="text-center" data-testid={`actions-${user.email}`}>
                                            <DropdownMenu>
                                                <DropdownMenuTrigger asChild>
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        className="um-row-menu no-default-hover-elevate"
                                                        aria-label={`Actions for ${user.email}`}
                                                        data-testid={`button-actions-${user.email}`}
                                                    >
                                                        <MoreHorizontal />
                                                    </Button>
                                                </DropdownMenuTrigger>
                                                <DropdownMenuContent align="end" className="um-menu">
                                                    <DropdownMenuItem
                                                        onClick={() => handleDeleteClick(user)}
                                                        className="um-menu-item"
                                                        data-tone="danger"
                                                        disabled={actionLoading}
                                                        data-testid={`button-delete-${user.email}`}
                                                    >
                                                        <Trash2 />
                                                        Revoke Invitation
                                                    </DropdownMenuItem>
                                                </DropdownMenuContent>
                                            </DropdownMenu>
                                        </TableCell>
                                    </TableRow>
                                );
                            }) : (
                                <TableRow className="um-state-row">
                                    <TableCell colSpan={8}>
                                        <div className="um-state">
                                            <span className="um-state-icon" aria-hidden="true">
                                                <UserPlus />
                                            </span>
                                            <p className="um-state-title">
                                                {searchTerm ? "No matching invitations found" : "No pending invitations"}
                                            </p>
                                            <p className="um-state-text">
                                                {searchTerm
                                                    ? "Try adjusting your search criteria."
                                                    : "Invite users to get started with the platform."}
                                            </p>
                                        </div>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </div>

                {filteredUsers.length > 0 && (
                    <div className="um-pagination">
                        <span className="um-pagination-summary">
                            Showing {((currentPage - 1) * ITEMS_PER_PAGE) + 1}–{Math.min(currentPage * ITEMS_PER_PAGE, filteredUsers.length)} of {filteredUsers.length} invitations
                        </span>
                        <CardPagination
                            label="Invitation pages"
                            currentPage={currentPage}
                            totalPages={Math.max(1, totalPages)}
                            onPageChange={handlePageChange}
                        />
                    </div>
                )}
            </div>

            <InviteUserDialog
                open={showInviteDialog}
                onOpenChange={setShowInviteDialog}
                availableAdminRoles={adminRoles}
                availableUserRoles={userRoles}
                availableMachines={allDevices}
                tenantName={tenantName}
                pendingUserCall={loadUsers}
                handleTab={(value: string) => { setActiveTab(value) }}
            />

            <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
                <AlertDialogContent className="um-dialog">
                    <AlertDialogHeader className="um-dialog-header">
                        <span className="um-dialog-icon" aria-hidden="true">
                            <Trash2 />
                        </span>
                        <div className="min-w-0">
                            <AlertDialogTitle className="um-dialog-title">Revoke Invitation</AlertDialogTitle>
                            <AlertDialogDescription className="um-dialog-description">
                                Are you sure you want to revoke the invitation for{" "}
                                <span className="font-semibold">
                                    {selectedUser?.firstName} {selectedUser?.lastName}
                                </span>
                                ? The verification code will no longer work and the user will not be able to complete signup.
                            </AlertDialogDescription>
                        </div>
                    </AlertDialogHeader>
                    <AlertDialogFooter className="um-dialog-footer">
                        <AlertDialogCancel disabled={actionLoading} className="um-dialog-button" data-testid="button-cancel-delete">
                            Cancel
                        </AlertDialogCancel>
                        <AlertDialogAction
                            onClick={handleDeleteConfirm}
                            disabled={actionLoading}
                            className="um-dialog-button bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            data-testid="button-confirm-delete"
                        >
                            Revoke Invitation
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
